import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { Subscription } from 'rxjs';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { SharedMethodsService } from 'src/app/services/shared-methods.service';
import { FisheryNoticesService } from 'src/app/services/storage/fishery-notices.service';
import { UserStorageService } from 'src/app/services/storage/user-storage.service';
import { UserData } from '../../data-models/user-models/user-data.model';
import { CommonDataService } from '../../services/storage/common-data.service';
import { EmailService } from '../../services/reporting/email.service';
import { MatDialog } from '@angular/material/dialog';
import packageJson from '../../../../package.json';


@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css'],
    standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {

  isLoggingIn: boolean = false; // flag that tells us if the user attemtping to login
  appVersion: string = packageJson.version;
  public resetPasswordEmail!: string;
  public isValidEmail!: boolean;

  // check online status
  isOnline: OnlineStatusType;
  onlineStatus: string = '';
  onlineBoolean: boolean = true;


  // current user data
  currUser: UserData = new UserData();

  // form validator error messages
  emailError: string = 'Required';
  passwordHide: boolean = true;
  onlineStatusSubscription!: Subscription;

  loginForm: UntypedFormGroup = new UntypedFormGroup({
    email: new FormControl<string>('', [Validators.required, Validators.email]),
    password: new FormControl<string>('', [Validators.required])
  });

  constructor(
    private authenticator: AuthenticationService,
    private router: Router,
    private onlineStatusService: OnlineStatusService,
    public sharedMethodsService: SharedMethodsService,
    private emailService: EmailService,
    private dialog: MatDialog
  ) {

    if (this.onlineStatusService.getStatus() === OnlineStatusType.ONLINE) {
      this.isOnline = OnlineStatusType.ONLINE;
      this.onlineStatus = 'online';
      this.onlineBoolean = true;
    }
    else {
      this.isOnline = OnlineStatusType.OFFLINE;
      this.onlineStatus = 'offline';
      this.onlineBoolean = false;

    }

  }


  /*  ---------- Initialization ---------- */


  ngOnInit(): void { // should separate out this code into separate methods

    // email validation
    this.loginForm.get('email')?.valueChanges.subscribe(() => {
      let emailErrors: ValidationErrors | null | undefined = this.loginForm.get('email')?.errors;
      if (emailErrors?.required) {
        this.emailError = 'Required';
      }
      else if (emailErrors?.email) {
        this.emailError = 'Invalid email format';
      }
      else {
        this.emailError = '';
      }
    });

    this.onlineStatusSubscription = this.onlineStatusService.status.subscribe((status: OnlineStatusType) => {
      if (status != this.isOnline) this.sharedMethodsService.reloadWindow();
      if (status === OnlineStatusType.ONLINE) {
        this.isOnline = OnlineStatusType.ONLINE;
        this.onlineStatus = 'online';
        this.onlineBoolean = true;
      }
      else {
        this.isOnline = OnlineStatusType.OFFLINE;
        this.onlineStatus = 'offline';
        this.onlineBoolean = false;
      }
    })

  }


  /*  ---------- Login Methods ---------- */


  // press the login button
  attemptLogin(): void {

    this.isLoggingIn = true; // set flag to show loading screen

    // attempt to login on/offline with entered credentials
    if (this.onlineStatusService.getStatus() === OnlineStatusType.ONLINE) {
      this.loginOnline();
    }
    else {
      this.loginOffline();
    }
    
  }

  // login or sign up online
  loginOnline(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      this.isLoggingIn = false;
      this.sharedMethodsService.toastLoginMissingError()
      return;
    }

    //Get login details and perform auth
    let email: string = (this.loginForm.get('email')?.value as string).toLowerCase();
    let password: string = this.loginForm.get('password')?.value;

    this.authenticator.postLogin(email, password).subscribe((serverResponse: any) => {
      //If successful, get id from the database, if user is not stored locally, fetch user data from couch and store it
      this.authenticator.getUserMetaData(email).subscribe((userData: any) => {
        let isAdmin = (userData?.roles ?? [] as string[]).includes('archipelago_admin');
        let user = UserData.transformAnonymous(userData.user);
        UserStorageService.getUserOnline(email, user, isAdmin).then(async () => {
          // Await the hash check so its 'user' doc write can't interleave with the
          // replication + conflict resolution that completeLogin kicks off.
          if (!isAdmin) await UserStorageService.checkPasswordHashMatch(password);
          // Forward the authoritative server user so its logbooks get re-applied
          // as the final local write, after the post-login sync.
          this.getUser(!isAdmin, user);

        });
      })
    }, (err: any) => {
      this.isLoggingIn = false;
      if (err.error.error == 'unauthorized') this.sharedMethodsService.toastLoginIncorrectEmailOrPasswordError();
    });

  }

  // login offline (need to have signed up online at least once)
  loginOffline(): void {

    if (this.loginForm.valid) {
      let username: string = this.loginForm.get('email')?.value;
      let password: string = this.loginForm.get('password')?.value;
      UserStorageService.getUserOffline(username, password).then((result: boolean) => {
        if (result) {
          this.getUser();
        } 
        else {
          this.sharedMethodsService.toastLoginUserNotFoundError();
          this.isLoggingIn = false;
        } 
      });
    }
    else {
      this.isLoggingIn = false;
      this.sharedMethodsService.toastLoginUserEmailRequiredError();
    }
  }

  // completes login by performing a data sync if online, then redirecting the user to their current homepage
  completeLogin(sync: boolean, serverUser?: UserData): void {

    if (sync) { // we should perform a data sync
      UserStorageService.updateConfirmationNumbers().then(() => {
        // Pass serverUser so getUserOnline re-applies the authoritative logbooks
        // AFTER conflict resolution, as the final local write. Prevents an older
        // replicated 'user' revision (without logbooks) from winning the LWW merge.
        UserStorageService.getUserOnline(this.currUser.userEmail, serverUser).then(() => {
          this.currUser = UserStorageService.currUser;
          if (!this.currUser.passedPrivacyNotice || !this.currUser.hasDisabledPrivacyNotice) {
            this.sharedMethodsService.openNoticeDialog().subscribe(response => {
              if (response) {
                this.currUser.passedPrivacyNotice = true;
                this.sharedMethodsService.openSkipPrivacyConfirmationDialog().subscribe(disable => {
                  this.currUser.hasDisabledPrivacyNotice = disable ?? false;
                  UserStorageService.saveUser(this.currUser, true);
                });

                CommonDataService.pullCommonData().then((result: boolean) => {
                  if (!result) { // display error if data cannot be synced
                    this.sharedMethodsService.toastDataSyncError();
                  }
                  AuthenticationService.setLogin(this.currUser.userEmail);
                  this.isLoggingIn = false;
                  this.router.navigate([this.currUser.currHomepage]); // complete login even if data cannot be synced for returning users
                });

              }
              this.isLoggingIn = false;
            });
          } else {
            CommonDataService.pullCommonData().then((result: boolean) => {
              if (!result) { // display error if data cannot be synced
                this.sharedMethodsService.toastDataSyncError();
              }
              AuthenticationService.setLogin(this.currUser.userEmail);
              this.isLoggingIn = false;
              this.router.navigate([this.currUser.currHomepage]); // complete login even if data cannot be synced for returning users
            });
          }
        })
      }).then(() => {
        FisheryNoticesService.checkForNotices(this.currUser.userEmail); // check for new notices (can do this as a background process)
      });
    } else { // we should not perform a data sync
      AuthenticationService.setLogin(this.currUser.userEmail);
      this.isLoggingIn = false;
      this.router.navigate([this.currUser.currHomepage]);
    }

  }


  /*  ---------- Helper Methods ---------- */


  // get user data from PouchDB
  getUser(sync: boolean = false, serverUser?: UserData): void {

    let error: string = UserStorageService.error;
    if (error && error !== 'not_found') {
      this.sharedMethodsService.toastUserNotStoredOnDeviceError();
      return;
    }
    this.currUser = UserStorageService.adminUser ?? UserStorageService.currUser;
    if (!this.currUser || !this.currUser.userEmail) {
      this.sharedMethodsService.toastLoginUserEmailNotDefined();
      return;
    }
    this.completeLogin(sync, serverUser);

  }


  /*  ---------- Password Reset Methods ---------- */



  checkValidEmail(event: string): boolean {
    const value: string = event;
    const pattern: RegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    this.isValidEmail = pattern.test(value);
    //check if email is associated with couchDB user, any way to do this besides an http get call using azure function app?
    return this.isValidEmail;
  }

  confirmToSend(): void {

    if (this.onlineStatusService.getStatus() === OnlineStatusType.ONLINE) { // only attempt to send the email if the user is online

      if (this.checkValidEmail(this.resetPasswordEmail)) {
        this.emailService.sendResetEmail(this.resetPasswordEmail).subscribe(() => {
        }, () => {
        });
        this.sharedMethodsService.toastEmailSent(); // Success message whether user exists or not to prevent enumeration attack
      } else {
        this.sharedMethodsService.toastInvalidForm();

      }

    } else {
      this.sharedMethodsService.toastOfflineError();

    }

    this.resetPasswordEmail = '';
    const buttonRef: HTMLElement | null = document.getElementById("closeBtn");
    buttonRef?.click();
   
  }

  toResources(): void {

    this.router.navigate(['resources']);

  }

  toHelp(): void {

    this.router.navigate(['help']);

  }


  ngOnDestroy(): void {

    if (this.onlineStatusSubscription) this.onlineStatusSubscription.unsubscribe();

  }

}
